import React, { useState, useEffect } from 'react';
import api from '../utils/axiosConfig';
import './PhoneManagement.css';

const PhoneManagement = () => {
  const [phones, setPhones] = useState([]);
  const [newPhone, setNewPhone] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch admin phones and companies on component mount
  useEffect(() => {
    fetchPhones();
    fetchCompanies();
  }, []);

  const fetchPhones = async () => {
    try {
      setLoading(true);
      const response = await api.get('/notifications/admin-phones');
      setPhones(response.data);
      setError('');
    } catch (err) {
      setError('Erro ao carregar telefones: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(response.data);
      
      // Set default company if only one exists
      if (response.data.length === 1) {
        setSelectedCompany(response.data[0]._id);
      }
    } catch (err) {
      setError('Erro ao carregar empresas: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleAddPhone = async (e) => {
    e.preventDefault();
    let phoneToAdd = newPhone.trim();
    
    if (!phoneToAdd) {
      setError('Por favor, insira um número de telefone');
      return;
    }

    if (!selectedCompany) {
      setError('Por favor, selecione uma empresa');
      return;
    }

    // Format phone number to ensure it has the correct format
    // Remove any non-digit characters except +
    phoneToAdd = phoneToAdd.replace(/[^0-9+]/g, '');
    
    // Ensure it starts with +55 for Brazilian numbers
    if (!phoneToAdd.startsWith('+')) {
      if (phoneToAdd.startsWith('55')) {
        phoneToAdd = '+' + phoneToAdd;
      } else {
        // Assume it's a Brazilian number
        phoneToAdd = '+55' + phoneToAdd;
      }
    }

    try {
      setLoading(true);
      const payload = selectedCompany === 'all' 
        ? { phone: phoneToAdd, allCompanies: true } 
        : { phone: phoneToAdd, companyId: selectedCompany };
      await api.post('/notifications/admin-phones', payload);
      setNewPhone('');
      setError('');
      fetchPhones(); // Refresh the list
    } catch (err) {
      setError('Erro ao adicionar telefone: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePhone = async (phone, companyId) => {
    if (!window.confirm('Tem certeza que deseja remover este número de telefone?')) {
      return;
    }

    try {
      setLoading(true);
      await api.delete(`/notifications/admin-phones/${encodeURIComponent(phone)}`, {
        data: { companyId }
      });
      setError('');
      fetchPhones(); // Refresh the list
    } catch (err) {
      setError('Erro ao remover telefone: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="phone-management-container">
      <h2>Gerenciar Telefones para Notificações</h2>
      <p className="description">
        Adicione números de telefone que receberão notificações sobre alterações de status de caminhões (somente descarregamento).
        Cada notificação incluirá um resumo explicativo com a quantidade de caminhões aguardando, em descarga e concluídos.
      </p>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="add-phone-section">
        <form onSubmit={handleAddPhone} className="add-phone-form">
          <div className="form-group">
            <label htmlFor="company">Empresa:</label>
            <select
              id="company"
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              disabled={loading || companies.length === 0}
            >
              <option value="">Selecione uma empresa</option>
              <option value="all">📌 TODAS AS EMPRESAS</option>
              {companies.map(company => (
                <option key={company._id} value={company._id}>
                  {company?.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="phone">Número de Telefone:</label>
            <input
              type="text"
              id="phone"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              placeholder="Ex: +5511999999999"
              disabled={loading}
            />
          </div>
          <button 
            type="submit" 
            className="btn-add"
            disabled={loading || !newPhone.trim() || !selectedCompany}
          >
            {loading ? 'Adicionando...' : 'Adicionar Telefone'}
          </button>
        </form>
      </div>
      
      <div className="phones-list-section">
        <h3>Telefones Cadastrados ({phones.length})</h3>
        {phones.length === 0 ? (
          <p>Nenhum telefone cadastrado.</p>
        ) : (
          <div className="phones-list">
            {phones.map((phoneObj, index) => {
              // Find company name for display
              const company = companies.find(c => c._id === phoneObj.companyId);
              return (
                <div key={index} className="phone-item">
                  <div className="phone-info">
                    <span className="phone-number">{phoneObj.phone}</span>
                    {phoneObj.allCompanies ? <span className="company-name all-companies">(📌 TODAS AS EMPRESAS)</span> : company && <span className="company-name">({company?.name})</span>}
                  </div>
                  <button 
                    className="btn-remove"
                    onClick={() => handleRemovePhone(phoneObj.phone, phoneObj.companyId)}
                    disabled={loading}
                  >
                    Remover
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PhoneManagement;